<template>
  <profile-edit-section-card
    v-if="localState"
    title="Experience"
    breadcrumb-label="Profile"
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    :has-invalid-input="hasInvalidInput"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <poppins-text-field
      id="years-experience"
      v-model="localState.yearsExperience"
      label-name="Years of paid housekeeping experience"
      type="text"
      pattern="[0-9]*"
      inputmode="numeric"
      :limit-input-to-pattern="true"
      :helper-text="yearsExperienceHelperText"
      :status="yearsExperienceStatus"
      class="mb-24"
    />
    <fieldset>
      <legend class="form__label pb-8">What housekeeping services do you have experience with?</legend>
      <p class="text-caption mb-16">Select all that apply</p>
      <ul>
        <li v-for="option in servicesExperienceOptions" :key="option.value" class="mb-8">
          <poppins-checkbox v-model="localState.servicesExperience" :label-name="option.label" :value="option.value" />
        </li>
      </ul>
    </fieldset>
  </profile-edit-section-card>
</template>

<script lang="ts">
import PoppinsTextField from '@/components/textfield/TextField.vue';
import PoppinsCheckbox from '@/components/checkbox/Checkbox.vue';
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import profileEditMixin from '../../mixins/profileEditMixin';
import { HOUSEKEEPING_SERVICES_EXPERIENCE_OPTIONS } from '../../__types__/CareTypeFormOptions';
import type { HousekeepingProfileFormState } from '../../__types__/HousekeepingProfileTypes';
import HelperStatus from '@/helpers/status';

export default {
  name: 'HousekeepingExperienceForm',
  components: {
    ProfileEditSectionCard,
    PoppinsTextField,
    PoppinsCheckbox,
  },
  mixins: [profileEditMixin],
  data(): any {
    return {
      localState: null,
      originalState: null,
      hasInvalidInput: false,
      yearsExperienceStatus: HelperStatus.Neutral,
    };
  },
  computed: {
    servicesExperienceOptions: () => HOUSEKEEPING_SERVICES_EXPERIENCE_OPTIONS,
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
    yearsExperienceHelperText(): string {
      if (!this.hasInvalidInput) return '';
      if (!this.localState?.yearsExperience) {
        return 'Years of experience is required';
      }
      if (isNaN(Number(this.localState.yearsExperience)) || Number(this.localState.yearsExperience) <= 0) {
        return 'Years of experience must be at least 1';
      }
      if (Number(this.localState.yearsExperience) > 50) {
        return 'Years of experience cannot be over 50';
      }
      return '';
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
    'localState.yearsExperience': function (newValue) {
      if (newValue && this.yearsExperienceStatus === HelperStatus.Error) {
        this.yearsExperienceStatus = HelperStatus.Neutral;
        this.hasInvalidInput = false;
      }
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    initializeFormState() {
      const state = this.formState as HousekeepingProfileFormState | null;
      if (!state) return;

      this.localState = {
        yearsExperience: String(state.yearsExperience ?? 0),
        servicesExperience: [...(state.servicesExperience || [])],
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    async handleSave() {
      if (!this.localState) return;
      if (
        !this.localState.yearsExperience ||
        isNaN(Number(this.localState.yearsExperience)) ||
        Number(this.localState.yearsExperience) <= 0 ||
        Number(this.localState.yearsExperience) > 50
      ) {
        this.yearsExperienceStatus = HelperStatus.Error;
        this.hasInvalidInput = true;
        this.$nextTick(() => {
          const element = document.getElementById('years-experience');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
        return;
      }
      if (this.hasUnsavedChanges) {
        await this.saveSection({
          yearsExperience: Number(this.localState.yearsExperience),
          experience: [...this.localState.servicesExperience],
        });
      }
    },
  },
};
</script>
